import './App.css';
import Hero from './components/hero.jsx';
import AboutSection from './components/about.jsx';
import FeatureSlider from './components/Feature.jsx';
import ContactForm from './components/contact.jsx';
import Footer from './components/footer.jsx';
import PrivacyPolicy from './components/PrivacyPolicy.jsx';
import PartnerUs from './components/PartnerUs.jsx';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

// Preload hero images for faster display
const heroImages = [
  '/1.jpg',
  '/3.jpg',
  '/2.jpg',
  '/4.jpg',
];

function preloadImages(urls) {
  urls.forEach((url) => {
    const img = new window.Image();
    img.src = url;
  });
}

function App() {
  const [showAbout, setShowAbout] = useState(false);
  const heroRef = useRef();
  const aboutRef = useRef();
  const featuresRef = useRef();
  const contactRef = useRef();
  const footerRef = useRef();
  const [parallax, setParallax] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Preload hero images on mount
  useEffect(() => {
    preloadImages(heroImages);
  }, []);

  // Responsive check
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Smooth, simple scroll-based transitions
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          const vh = window.innerHeight;
          setParallax(Math.min(scrollY / vh, 1));
          setShowAbout(scrollY >= vh * 0.5);

          // Only animate hero and about for simplicity
          if (heroRef.current) {
            heroRef.current.style.transform = `translateY(${-scrollY * 0.04}px) scale(${1 - Math.min(scrollY / vh, 0.05)})`;
            heroRef.current.style.opacity = 1 - Math.min(scrollY / (vh * 1.2), 0.2);
          }
          if (aboutRef.current) {
            aboutRef.current.style.transform = `translateY(${-scrollY * 0.06}px)`;
            aboutRef.current.style.opacity = 1 - Math.min(scrollY / (vh * 2), 0.2);
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleScrollToAbout = () => {
    const aboutSection = document.getElementById('about-section');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Responsive styles
  const heroStyle = {
    position: isMobile ? 'relative' : 'fixed',
    top: isMobile ? undefined : 0,
    left: isMobile ? undefined : 0,
    width: isMobile ? '100%' : '100vw',
    height: isMobile ? 'auto' : '100vh',
    zIndex: 10,
    pointerEvents: showAbout ? 'none' : 'auto',
    opacity: showAbout && !isMobile ? 0 : 1,
    transition: 'opacity 0.6s',
    willChange: 'transform, opacity',
    background: '#0a1128',
  };
  const aboutStyle = {
    position: 'relative',
    zIndex: 1,
    marginTop: isMobile ? 0 : '100vh',
    minHeight: '100vh',
    willChange: 'transform',
    background: '#0a1128',
  };

  useEffect(() => {
    document.title = 'Irod | Home';
  }, []);

  return (
    <Router>
      <div style={{ minHeight: '300vh', position: 'relative', overflow: isMobile ? 'visible' : 'hidden', background: '#0a1128' }}>
        {/* Navbar with Partner Us link 
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', zIndex: 100, background: 'transparent', borderBottom: '1px solid #1e293b22', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '10px 12px' : '16px 40px' }}>
          <Link to="/" style={{ color: '#fff', fontWeight: 700, fontSize: isMobile ? '1.1rem' : '1.3rem', textDecoration: 'none', letterSpacing: '0.01em' }}>IROD</Link>
          <div style={{ display: 'flex', gap: isMobile ? 12 : 28, alignItems: 'center' }}>
            <a href="#about-section" style={{ color: '#b6c3e0', textDecoration: 'none', fontWeight: 500 }}>About</a>
            <a href="#features-section" style={{ color: '#b6c3e0', textDecoration: 'none', fontWeight: 500 }}>Features</a>
            <a href="#contact-section" style={{ color: '#b6c3e0', textDecoration: 'none', fontWeight: 500 }}>Contact</a>
            <Link to="/partner-us" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>Partner Us</Link>
            <Link to="/privacy-policy" style={{ color: '#b6c3e0', textDecoration: 'none', fontWeight: 500, fontSize: '0.98em' }}>Privacy</Link>
          </div>
        </div>*/}
        <div style={{ height: isMobile ? 56 : 72 }} />
        <Routes>
          <Route path="/" element={
            <>
              <div ref={heroRef} style={heroStyle}>
                <Hero onScrollToAbout={handleScrollToAbout}
                  navigationLinks={[
                    { name: 'About', href: '#about-section', type: 'anchor' },
                    { name: 'Features', href: '#features-section', type: 'anchor' },
                    { name: 'Contact', href: '#contact-section', type: 'anchor' },
                    { name: 'Partner Us', href: '/partner-us', type: 'route' },
                    { name: 'Privacy', href: '/privacy-policy', type: 'route' },
                  ]}
                />
              </div>
              <div ref={aboutRef} style={aboutStyle} id="about-section">
                <AboutSection />
              </div>
              <div ref={featuresRef} style={{ position: 'relative', zIndex: 1, minHeight: '100vh', background: 'transparent' }} id="features-section">
                <FeatureSlider />
              </div>
              <div ref={contactRef} style={{ position: 'relative', zIndex: 1, minHeight: '100vh', background: 'transparent', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: isMobile ? '24px 0' : '48px 0' }} id="contact-section">
                <div style={{
                  background: 'rgba(18, 24, 38, 0.92)',
                  borderRadius: '1.5rem',
                  boxShadow: '0 4px 32px 0 rgba(30,40,90,0.13)',
                  padding: isMobile ? '20px 8px' : '40px 32px',
                  width: isMobile ? '98vw' : '540px',
                  maxWidth: '98vw',
                  margin: '0 auto',
                  border: '1.5px solid #1e293b22',
                  backdropFilter: 'blur(6px)',
                  transition: 'box-shadow 0.3s',
                }}>
                  <ContactForm />
                </div>
              </div>
              <div style={{ marginTop: isMobile ? '80px' : '200px' }} />
              <div ref={footerRef}>
                <Footer />
              </div>
            </>
          } />
          <Route path="/privacy-policy" element={<><PageTitle title="Privacy Policy" /><PrivacyPolicy /></>} />
          <Route path="/partner-us" element={<><PageTitle title="Partner Us" /><PartnerUs /></>} />
        </Routes>
      </div>
    </Router>
  );
}

function PageTitle({ title }) {
  useEffect(() => {
    document.title = `Irod | ${title}`;
  }, [title]);
  return null;
}

export default App;
